<template>
  <section class="form-section">
    <div class="section-header">
      <h2 class="section-title">Skills & Interests</h2>
      <ContextMenu
        :is-authenticated="isAuthenticated"
        add-new-label="Add New Category"
        add-from-master-label="Add from Profile"
        @add-new="addCategory"
        @add-from-master="handleAddFromMaster"
      />
    </div>
    
    <div v-for="(category, index) in skillCategories" :key="index" class="form-card">
      <div class="card-header">
        <h3 class="card-title">Category {{ index + 1 }}</h3>
        <div class="flex items-center space-x-1 sm:space-x-2">
          <button 
            type="button" 
            @click="moveCategoryUp(index)" 
            class="btn-reorder" 
            v-if="index > 0"
            title="Move up"
          >
            <svg class="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"></path>
            </svg>
          </button>
          <button 
            type="button" 
            @click="moveCategoryDown(index)" 
            class="btn-reorder" 
            v-if="index < skillCategories.length - 1"
            title="Move down"
          >
            <svg class="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
          <button type="button" @click="removeCategory(index)" class="btn-remove" v-if="skillCategories.length > 1" title="Delete">
            <svg class="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
            </svg>
          </button>
        </div>
      </div>
      
      <div class="form-grid">
        <div class="form-group">
          <label class="form-label">Category Name</label>
          <input 
            type="text" 
            :value="category.name"
            @input="updateCategoryField(index, 'name', $event.target.value)"
            :class="['form-input', { 'field-needs-review': isFieldHighlighted(index, 'name') }]"
            placeholder="Technologies, Languages, Programming Languages, etc."
            required
          />
          <div v-if="isFieldHighlighted(index, 'name')" class="review-badge mt-1">
            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Missing or unclear data - please verify
          </div>
        </div>
        <div class="form-group">
          <label class="form-label">Content</label>
          <input
            :value="category.content"
            @input="updateCategoryField(index, 'content', $event.target.value)"
            :class="['form-input', { 'field-needs-review': isFieldHighlighted(index, 'content') }]"
            placeholder="PowerPoint, Excel, Word, Python, SQL, etc."
            type="text"
            required
          />
          <div v-if="isFieldHighlighted(index, 'content')" class="review-badge mt-1">
            <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            Missing or unclear data - please verify
          </div>
        </div>
      </div>
    </div>

    <!-- Master Data Modal -->
    <MasterDataModal 
      :is-open="showMasterDataModal"
      data-type="skills"
      @close="closeMasterDataModal"
      @items-selected="handleMasterDataSelection"
    />
  </section>
</template>

<script>
import MasterDataModal from './MasterDataModal.vue'
import ContextMenu from './ContextMenu.vue'

export default {
  name: 'SkillCategoriesForm',
  components: {
    MasterDataModal,
    ContextMenu
  },
  props: {
    skillCategories: {
      type: Array,
      required: true,
      default: () => [{
        name: '',
        content: ''
      }]
    },
    isAuthenticated: {
      type: Boolean,
      default: false
    },
    highlightedFields: {
      type: Array,
      default: () => []
    }
  },
  emits: ['update:skill-categories', 'field-changed', 'scroll-to-new'],
  data() {
    return {
      showMasterDataModal: false
    }
  },
  methods: {
    updateCategoryField(index, field, value) {
      const updatedList = [...this.skillCategories]
      updatedList[index] = { ...updatedList[index], [field]: value }
      this.$emit('update:skill-categories', updatedList)
      this.$emit('field-changed')
    },

    addCategory() {
      const updatedList = [...this.skillCategories, {
        name: '',
        content: ''
      }]
      this.$emit('update:skill-categories', updatedList)
      this.$emit('field-changed')
      
      // Emit scroll event with new entry index
      this.$nextTick(() => {
        this.$emit('scroll-to-new', { type: 'skills', index: updatedList.length - 1 })
      })
    },

    removeCategory(index) {
      if (this.skillCategories.length > 1) {
        const updatedList = [...this.skillCategories]
        updatedList.splice(index, 1)
        this.$emit('update:skill-categories', updatedList)
        this.$emit('field-changed')
      }
    },

    moveCategoryUp(index) {
      if (index > 0) {
        const updatedList = [...this.skillCategories]
        const item = updatedList.splice(index, 1)[0]
        updatedList.splice(index - 1, 0, item)
        this.$emit('update:skill-categories', updatedList)
        this.$emit('field-changed')
        
        // Emit scroll event to follow the moved entry
        this.$nextTick(() => {
          this.$emit('scroll-to-new', { type: 'skills', index: index - 1 })
        })
      }
    },

    moveCategoryDown(index) {
      if (index < this.skillCategories.length - 1) {
        const updatedList = [...this.skillCategories]
        const item = updatedList.splice(index, 1)[0]
        updatedList.splice(index + 1, 0, item)
        this.$emit('update:skill-categories', updatedList)
        this.$emit('field-changed')
        
        // Emit scroll event to follow the moved entry
        this.$nextTick(() => {
          this.$emit('scroll-to-new', { type: 'skills', index: index + 1 })
        })
      }
    },

    handleAddFromMaster() {
      this.showMasterDataModal = true
    },

    handleMasterDataSelection(data) {
      // Convert master data items to skill category format
      const newSkillCategories = data.items.map(item => ({
        name: item.category_name || '',
        content: item.content || ''
      }))

      // Add the new entries to the existing list
      const updatedList = [...this.skillCategories, ...newSkillCategories]
      this.$emit('update:skill-categories', updatedList)
      this.$emit('field-changed')

      // Scroll to the first new entry
      if (newSkillCategories.length > 0) {
        this.$nextTick(() => {
          this.$emit('scroll-to-new', { 
            type: 'skills', 
            index: this.skillCategories.length 
          })
        })
      }
    },

    closeMasterDataModal() {
      this.showMasterDataModal = false
    },

    isFieldHighlighted(index, fieldName) {
      const highlightedEntry = this.highlightedFields.find(entry => entry.index === index)
      return highlightedEntry && highlightedEntry.fields.includes(fieldName)
    }
  }
}
</script>

<style scoped>
/* Field Highlighting Styles */
.field-needs-review {
  @apply border-2 border-red-500 dark:border-red-400 bg-red-50 dark:bg-red-900/20;
}

.field-needs-review:focus {
  @apply border-red-600 dark:border-red-500 ring-2 ring-red-200 dark:ring-red-800;
}

.review-badge {
  @apply inline-flex items-center text-xs font-medium text-red-600;
}
/* No additional styles needed - uses shared form styling like other components */
</style>